import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import {
  ArrowLeft, Brain, Check, X, Pencil, Plus,
  ChevronRight, ChevronDown, AlertCircle, Clock, CheckCircle2, XCircle,
  Lightbulb, Target, Route, HelpCircle, Sparkles, RotateCcw, BookOpen,
} from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────

interface ThinkingContent {
  recommendation: string;
  reasoning: string;
  approach: string;
  assumptions: string[];
  outputs_if_approved: string[];
}

interface SamEdit {
  section: string;
  original: string;
  edit: string;
  edited_at: string;
}

interface ThinkingSession {
  id: string;
  org_id: string;
  trigger_type: string;
  thinking_text: string;
  sam_edits: SamEdit[];
  status: string;
  rejection_reason: string | null;
  created_at: string;
  approved_at: string | null;
}

type ActiveView = 'empty' | 'generating' | 'preview' | 'editing' | 'approved' | 'rejected';

// ── Helpers ───────────────────────────────────────────────────────

function parseThinking(raw: string): ThinkingContent {
  try {
    const p = JSON.parse(raw);
    return {
      recommendation: p.recommendation ?? '',
      reasoning: p.reasoning ?? '',
      approach: p.approach ?? '',
      assumptions: Array.isArray(p.assumptions) ? p.assumptions : [],
      outputs_if_approved: Array.isArray(p.outputs_if_approved) ? p.outputs_if_approved : [],
    };
  } catch {
    return { recommendation: raw, reasoning: '', approach: '', assumptions: [], outputs_if_approved: [] };
  }
}

const TRIGGER_OPTIONS = [
  { value: 'manual',               label: 'Manual / Ad-hoc' },
  { value: 'weekly_recommendation',label: 'Weekly Recommendation' },
  { value: 'user_at_risk',         label: 'User at Risk' },
  { value: 'experiment_needed',    label: 'Experiment Needed' },
  { value: 'health_flag_high',     label: 'Health Flag — High Severity' },
  { value: 'knowledge_incomplete', label: 'Knowledge Not Set Up' },
  { value: 'comms_gap_14d',        label: 'Comms Gap (14 days)' },
];

const STATUS_META: Record<string, { label: string; color: string; icon: React.ComponentType<{ className?: string }> }> = {
  draft:    { label: 'Draft',    color: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/30', icon: Clock },
  approved: { label: 'Approved', color: 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30',   icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30',           icon: XCircle },
};

// ── Section definitions ───────────────────────────────────────────

const SECTIONS = [
  {
    key: 'recommendation' as const,
    label: "What I'm Recommending",
    icon: Target,
    isList: false,
    hint: 'The specific action Cosmo is proposing',
  },
  {
    key: 'reasoning' as const,
    label: "Why I'm Recommending This",
    icon: Lightbulb,
    isList: false,
    hint: 'The data and logic behind the recommendation',
  },
  {
    key: 'approach' as const,
    label: 'My Approach',
    icon: Route,
    isList: false,
    hint: 'How Cosmo plans to execute — the specific method',
  },
  {
    key: 'assumptions' as const,
    label: "What I'm Assuming",
    icon: HelpCircle,
    isList: true,
    hint: 'One assumption per line. Edit to correct wrong assumptions.',
  },
  {
    key: 'outputs_if_approved' as const,
    label: "What I'll Generate If You Approve",
    icon: Sparkles,
    isList: true,
    hint: 'One output per line.',
  },
] as const;

// ── Main component ────────────────────────────────────────────────

export default function ThinkingLayer() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionIdParam = searchParams.get('session');

  // Org state
  const [orgName, setOrgName] = useState('');

  // Sessions
  const [sessions, setSessions]             = useState<ThinkingSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<ThinkingSession | null>(null);
  const [loadingData, setLoadingData]       = useState(true);

  // Active view
  const [activeView, setActiveView] = useState<ActiveView>('empty');

  // Generate panel
  const [triggerType, setTriggerType]   = useState('manual');
  const [contextNote, setContextNote]   = useState('');
  const [generating, setGenerating]     = useState(false);

  // Edit state
  const [editingKey, setEditingKey]     = useState<typeof SECTIONS[number]['key'] | null>(null);
  const [editText, setEditText]         = useState('');
  const [editApplied, setEditApplied]   = useState(false);
  const [applyingEdit, setApplyingEdit] = useState(false);

  // Reject state
  const [showRejectForm, setShowRejectForm]   = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  // Outputs
  const [outputs, setOutputs] = useState<any[]>([]);

  // Knowledge context
  const [knowledgeItems, setKnowledgeItems]       = useState<{ field_type: string; content_json: any }[]>([]);
  const [knowledgeExpanded, setKnowledgeExpanded] = useState(false);

  // ── Data loading ─────────────────────────────────────────────────

  const loadSessions = useCallback(async () => {
    if (!orgId) return;
    const { data, error } = await supabase
      .from('cosmo_thinking_sessions')
      .select('*')
      .eq('org_id', orgId)
      .order('created_at', { ascending: false });
    if (error) { console.error(error); return; }
    // Cast sam_edits to our typed array
    const typed = (data || []).map(s => ({
      ...s,
      sam_edits: Array.isArray(s.sam_edits) ? (s.sam_edits as unknown as SamEdit[]) : [],
    }));
    setSessions(typed);
    return typed;
  }, [orgId]);

  const loadOutputs = useCallback(async (sessionId: string) => {
    const { data } = await supabase
      .from('cosmo_outputs')
      .select('*')
      .eq('thinking_session_id', sessionId)
      .order('created_at', { ascending: true });
    setOutputs(data || []);
  }, []);

  useEffect(() => {
    if (!orgId) return;
    const init = async () => {
      setLoadingData(true);
      // Load org name + knowledge store in parallel
      const [orgData, knowledgeData] = await Promise.all([
        supabase.rpc('get_org_admin_detail', { _org_id: orgId }),
        supabase
          .from('org_knowledge_store')
          .select('field_type, content_json')
          .eq('org_id', orgId),
      ]);
      if (orgData.data?.[0]) setOrgName(orgData.data[0].org_name);
      setKnowledgeItems(knowledgeData.data ?? []);

      // Load sessions
      const typed = await loadSessions();

      // Auto-select: prefer session from URL param, then most recent draft, then first
      if (typed && typed.length > 0) {
        const byParam = sessionIdParam ? typed.find(s => s.id === sessionIdParam) : null;
        const draft   = typed.find(s => s.status === 'draft');
        const toSelect = byParam ?? draft ?? typed[0];
        selectSession(toSelect);
      }
      setLoadingData(false);
    };
    init();
  }, [orgId]);

  const selectSession = (session: ThinkingSession) => {
    setSelectedSession(session);
    setEditingKey(null);
    setEditText('');
    setEditApplied(false);
    setShowRejectForm(false);
    setRejectionReason('');
    if (session.status === 'approved') {
      setActiveView('approved');
      loadOutputs(session.id);
    } else if (session.status === 'rejected') {
      setActiveView('rejected');
    } else {
      setActiveView('preview');
    }
  };

  // ── Generate ──────────────────────────────────────────────────────

  const handleGenerate = async () => {
    if (!orgId) return;
    setGenerating(true);
    setActiveView('generating');

    try {
      // Build the placeholder content so the session always has valid JSON
      const placeholderContent: ThinkingContent = {
        recommendation: contextNote.trim()
          ? contextNote.trim()
          : `New ${TRIGGER_OPTIONS.find(t => t.value === triggerType)?.label ?? triggerType} for ${orgName}`,
        reasoning: 'Generating…',
        approach: 'Generating…',
        assumptions: ['Generating…'],
        outputs_if_approved: ['Generating…'],
      };

      // Insert the session row first
      const { data: newSession, error: insertErr } = await supabase
        .from('cosmo_thinking_sessions')
        .insert({
          org_id: orgId,
          trigger_type: triggerType,
          thinking_text: JSON.stringify(placeholderContent),
          status: 'draft',
        })
        .select()
        .single();

      if (insertErr) throw insertErr;

      // Try to call the edge function
      const { data: genData, error: genErr } = await supabase.functions.invoke('generate-thinking', {
        body: { org_id: orgId, thinking_session_id: newSession.id, trigger_type: triggerType, context_note: contextNote },
      });

      if (genErr) {
        // Edge function not deployed yet — surface a warning and use a richer placeholder
        const draftContent: ThinkingContent = {
          recommendation: contextNote.trim() || `[Edit this section — describe what Cosmo should recommend]`,
          reasoning: '[Edit this section — explain why this is the right move based on what you know about this org]',
          approach: '[Edit this section — describe how you\'d execute the recommendation]',
          assumptions: ['[Add assumptions here — one per line]'],
          outputs_if_approved: ['[List what should be produced when approved]'],
        };
        await supabase
          .from('cosmo_thinking_sessions')
          .update({ thinking_text: JSON.stringify(draftContent) })
          .eq('id', newSession.id);
        toast.warning('generate-thinking edge function not deployed yet — edit sections to fill in the thinking manually.');
      } else if (genData?.thinking_content) {
        // Edge function returned structured content — update the row
        await supabase
          .from('cosmo_thinking_sessions')
          .update({ thinking_text: JSON.stringify(genData.thinking_content) })
          .eq('id', newSession.id);
      }

      // Reload and select the new session
      const refreshed = await loadSessions();
      const fresh = refreshed?.find(s => s.id === newSession.id) ?? { ...newSession, sam_edits: [] };
      selectSession(fresh as ThinkingSession);
      setContextNote('');
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || 'Failed to create thinking session');
      setActiveView('empty');
    } finally {
      setGenerating(false);
    }
  };

  // ── Edit ──────────────────────────────────────────────────────────

  const startEdit = (key: typeof SECTIONS[number]['key']) => {
    if (!selectedSession) return;
    const content = parseThinking(selectedSession.thinking_text);
    const current = content[key];
    setEditingKey(key);
    setEditText(Array.isArray(current) ? current.join('\n') : current);
    setEditApplied(false);
    setActiveView('editing');
  };

  const applyEdit = async () => {
    if (!selectedSession || !editingKey) return;
    setApplyingEdit(true);
    try {
      const content = parseThinking(selectedSession.thinking_text);
      const section = SECTIONS.find(s => s.key === editingKey)!;

      // Build updated content
      const updatedContent: ThinkingContent = { ...content };
      if (section.isList) {
        updatedContent[editingKey] = editText
          .split('\n')
          .map(l => l.trim())
          .filter(Boolean) as any;
      } else {
        (updatedContent as any)[editingKey] = editText;
      }

      // Record the edit in sam_edits
      const newEdit: SamEdit = {
        section: editingKey,
        original: Array.isArray(content[editingKey])
          ? JSON.stringify(content[editingKey])
          : (content[editingKey] as string),
        edit: editText,
        edited_at: new Date().toISOString(),
      };
      const existingEdits = selectedSession.sam_edits ?? [];
      const updatedEdits = [...existingEdits, newEdit];

      const { error } = await supabase
        .from('cosmo_thinking_sessions')
        .update({
          thinking_text: JSON.stringify(updatedContent),
          sam_edits: updatedEdits as any,
        })
        .eq('id', selectedSession.id);
      if (error) throw error;

      setSelectedSession(prev =>
        prev ? { ...prev, thinking_text: JSON.stringify(updatedContent), sam_edits: updatedEdits } : prev
      );
      setEditApplied(true);
      toast.success('Edit applied to thinking');
    } catch (err: any) {
      toast.error('Failed to apply edit');
    } finally {
      setApplyingEdit(false);
    }
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditText('');
    setEditApplied(false);
    setActiveView('preview');
  };

  // ── Approve ───────────────────────────────────────────────────────

  const approveSession = async () => {
    if (!selectedSession) return;
    try {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from('cosmo_thinking_sessions')
        .update({ status: 'approved', approved_at: now })
        .eq('id', selectedSession.id);
      if (error) throw error;
      setSelectedSession(prev => prev ? { ...prev, status: 'approved', approved_at: now } : prev);
      setSessions(prev => prev.map(s => s.id === selectedSession.id ? { ...s, status: 'approved', approved_at: now } : s));
      await loadOutputs(selectedSession.id);
      setActiveView('approved');
      toast.success('Thinking approved — Cosmo can now generate outputs');
    } catch (err: any) {
      toast.error('Failed to approve session');
    }
  };

  // ── Reject ────────────────────────────────────────────────────────

  const rejectSession = async () => {
    if (!selectedSession) return;
    try {
      const { error } = await supabase
        .from('cosmo_thinking_sessions')
        .update({ status: 'rejected', rejection_reason: rejectionReason || null })
        .eq('id', selectedSession.id);
      if (error) throw error;
      setSelectedSession(prev => prev ? { ...prev, status: 'rejected' } : prev);
      setSessions(prev => prev.map(s => s.id === selectedSession.id ? { ...s, status: 'rejected' } : s));
      setShowRejectForm(false);
      setRejectionReason('');
      setActiveView('rejected');
      toast.success('Thinking dismissed');
    } catch (err: any) {
      toast.error('Failed to reject session');
    }
  };

  // ── Render helpers ────────────────────────────────────────────────

  const StatusBadge = ({ status }: { status: string }) => {
    const meta = STATUS_META[status] ?? STATUS_META.draft;
    const Icon = meta.icon;
    return (
      <Badge variant="outline" className={`text-xs gap-1 ${meta.color}`}>
        <Icon className="h-3 w-3" />
        {meta.label}
      </Badge>
    );
  };

  // ── Panels ────────────────────────────────────────────────────────

  const renderGeneratePanel = () => (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
            <Brain className="h-6 w-6 text-primary" />
          </div>
          <h2 className="text-lg font-semibold">Generate Cosmo's Thinking</h2>
          <p className="text-sm text-muted-foreground">
            Before any output is produced, Cosmo shows its plan. You review it, correct wrong
            assumptions, then approve execution.
          </p>
        </div>

        <div className="space-y-4 bg-muted/30 border rounded-xl p-5">
          <div className="space-y-2">
            <Label>What triggered this thinking?</Label>
            <Select value={triggerType} onValueChange={setTriggerType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TRIGGER_OPTIONS.map(o => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>
              Context for Cosmo{' '}
              <span className="text-muted-foreground text-xs font-normal">(optional)</span>
            </Label>
            <Textarea
              value={contextNote}
              onChange={e => setContextNote(e.target.value)}
              placeholder={`e.g. "Tom Ellis hasn't logged in for 9 days. Priya Nair never activated. I want to re-engage both before the 30-seat expansion starts."`}
              rows={3}
            />
          </div>

          <Button
            className="w-full"
            onClick={handleGenerate}
            disabled={generating}
          >
            <Brain className="h-4 w-4 mr-2" />
            {generating ? 'Generating…' : 'Generate Cosmo\'s Thinking'}
          </Button>
        </div>
      </div>
    </div>
  );

  const renderGeneratingState = () => (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center space-y-3">
        <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto animate-pulse">
          <Brain className="h-6 w-6 text-primary" />
        </div>
        <p className="text-sm font-medium">Cosmo is thinking…</p>
        <p className="text-xs text-muted-foreground">Reading org context and formulating a plan</p>
      </div>
    </div>
  );

  const renderPreviewPanel = () => {
    if (!selectedSession) return null;
    const content = parseThinking(selectedSession.thinking_text);
    const editCount = selectedSession.sam_edits?.length ?? 0;

    return (
      <div className="flex-1 flex flex-col min-h-0">
        {/* Panel header */}
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
          <div className="flex items-center gap-3">
            <StatusBadge status={selectedSession.status} />
            <span className="text-sm text-muted-foreground">
              {TRIGGER_OPTIONS.find(t => t.value === selectedSession.trigger_type)?.label ?? selectedSession.trigger_type}
            </span>
            {editCount > 0 && (
              <Badge variant="outline" className="text-xs text-muted-foreground gap-1">
                <Pencil className="h-3 w-3" />
                {editCount} edit{editCount > 1 ? 's' : ''} applied
              </Badge>
            )}
          </div>
          <span className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(selectedSession.created_at), { addSuffix: true })}
          </span>
        </div>

        {/* Intro line */}
        <div className="px-6 pt-4 pb-2 shrink-0">
          <p className="text-sm text-muted-foreground italic">
            "Before I generate anything, here's my plan. Correct anything that's wrong before I proceed."
          </p>
        </div>

        {/* Knowledge context collapsible */}
        <div className="px-6 pb-2 shrink-0">
          <button
            onClick={() => setKnowledgeExpanded(v => !v)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors text-left"
          >
            <BookOpen className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="text-xs font-medium flex-1">
              Org Context
              {knowledgeItems.length > 0
                ? ` · ${knowledgeItems.length} knowledge entr${knowledgeItems.length === 1 ? 'y' : 'ies'}`
                : ' · No knowledge stored yet'}
            </span>
            <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${knowledgeExpanded ? 'rotate-180' : ''}`} />
          </button>

          {knowledgeExpanded && (
            <div className="mt-2 border rounded-lg divide-y bg-muted/10">
              {knowledgeItems.length === 0 ? (
                <div className="px-4 py-3 text-xs text-muted-foreground flex items-center justify-between">
                  <span>No knowledge stored for this org yet.</span>
                  <button
                    onClick={() => navigate(`/admin/org/${orgId}`)}
                    className="text-primary hover:underline"
                  >
                    Set it up →
                  </button>
                </div>
              ) : (
                knowledgeItems.map((item, i) => {
                  const json = item.content_json as Record<string, any> | null;
                  const label = item.field_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                  return (
                    <div key={i} className="px-4 py-2.5 space-y-1">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
                      {json ? (
                        <ul className="space-y-0.5">
                          {Object.entries(json)
                            .filter(([, v]) => v !== null && v !== '' && !(Array.isArray(v) && v.length === 0))
                            .slice(0, 4)
                            .map(([k, v]) => (
                              <li key={k} className="text-xs text-muted-foreground flex gap-2">
                                <span className="font-medium capitalize shrink-0">{k.replace(/_/g, ' ')}:</span>
                                <span className="truncate">
                                  {Array.isArray(v)
                                    ? (v as any[]).slice(0, 2).map((x: any) => (typeof x === 'object' ? x?.name ?? JSON.stringify(x) : String(x))).join(', ')
                                    : String(v)}
                                </span>
                              </li>
                            ))}
                        </ul>
                      ) : (
                        <p className="text-xs text-muted-foreground italic">Empty</p>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* Thinking sections */}
        <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-3">
          {SECTIONS.map(section => {
            const value = content[section.key];
            const isEmpty = Array.isArray(value) ? value.length === 0 || (value.length === 1 && !value[0]) : !value;
            const Icon = section.icon;

            return (
              <div key={section.key} className="border rounded-xl p-4 group">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2.5 flex-1 min-w-0">
                    <div className="mt-0.5 h-6 w-6 rounded-md bg-muted flex items-center justify-center shrink-0">
                      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5">
                        {section.label}
                      </p>
                      {isEmpty ? (
                        <p className="text-sm text-muted-foreground italic">Not specified</p>
                      ) : section.isList ? (
                        <ul className="space-y-1.5">
                          {(value as string[]).map((item, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm">
                              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-muted-foreground shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm leading-relaxed">{value as string}</p>
                      )}
                    </div>
                  </div>
                  {selectedSession.status === 'draft' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                      onClick={() => startEdit(section.key)}
                    >
                      <Pencil className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Action bar — draft only */}
        {selectedSession.status === 'draft' && (
          <div className="border-t px-6 py-4 shrink-0">
            {showRejectForm ? (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-sm">Why are you rejecting this?</Label>
                  <Textarea
                    value={rejectionReason}
                    onChange={e => setRejectionReason(e.target.value)}
                    placeholder="Optional — helps Cosmo improve future thinking for this org"
                    rows={2}
                    autoFocus
                  />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setShowRejectForm(false)}>
                    Cancel
                  </Button>
                  <Button variant="destructive" size="sm" onClick={rejectSession}>
                    Confirm Reject
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Button className="flex-1" onClick={approveSession}>
                  <Check className="h-4 w-4 mr-2" />
                  Approve — Generate Outputs
                </Button>
                <Button variant="outline" onClick={() => startEdit('approach')}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit Thinking
                </Button>
                <Button
                  variant="ghost"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => setShowRejectForm(true)}
                >
                  <X className="h-4 w-4 mr-1" />
                  Not Now
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderEditPanel = () => {
    if (!selectedSession || !editingKey) return null;
    const content = parseThinking(selectedSession.thinking_text);
    const section = SECTIONS.find(s => s.key === editingKey)!;
    const Icon = section.icon;
    const original = content[editingKey];
    const originalDisplay = Array.isArray(original) ? original.join('\n') : original;
    const updatedContent = parseThinking(selectedSession.thinking_text);

    return (
      <div className="flex-1 flex flex-col min-h-0">
        {/* Edit header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b shrink-0">
          <Button variant="ghost" size="sm" onClick={cancelEdit}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div className="flex items-center gap-2 text-sm font-medium">
            <Icon className="h-4 w-4 text-muted-foreground" />
            Editing: {section.label}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Original */}
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Cosmo's Original
            </p>
            <div className="bg-muted/40 border rounded-lg p-3 text-sm text-muted-foreground leading-relaxed">
              {originalDisplay || <em>Empty</em>}
            </div>
          </div>

          {/* Sam's edit */}
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Your Edit
            </p>
            <p className="text-xs text-muted-foreground">{section.hint}</p>
            <Textarea
              value={editText}
              onChange={e => setEditText(e.target.value)}
              rows={section.isList ? 5 : 4}
              placeholder={
                section.isList
                  ? 'One item per line…'
                  : 'Correct or add to Cosmo\'s thinking…'
              }
              autoFocus
            />
          </div>

          {/* Applied confirmation */}
          {editApplied && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-green-700 dark:text-green-400">
                <CheckCircle2 className="h-4 w-4" />
                Edit applied to thinking
              </div>
              <div className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Updated: {section.label}
                </p>
                {section.isList ? (
                  <ul className="space-y-1">
                    {(updatedContent[editingKey] as string[]).map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-green-500 shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm">{updatedContent[editingKey] as string}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Edit action bar */}
        <div className="border-t px-6 py-4 shrink-0">
          {editApplied ? (
            <div className="flex gap-2">
              <Button className="flex-1" onClick={approveSession}>
                <Check className="h-4 w-4 mr-2" />
                Approve Revised Plan
              </Button>
              <Button variant="outline" onClick={() => { setEditApplied(false); setEditText(''); }}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Keep Editing
              </Button>
              <Button variant="ghost" onClick={cancelEdit}>
                <ArrowLeft className="h-4 w-4 mr-1" />
                View Full Plan
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button
                className="flex-1"
                onClick={applyEdit}
                disabled={!editText.trim() || applyingEdit}
              >
                {applyingEdit ? 'Applying…' : 'Apply Edit → Revise Plan'}
              </Button>
              <Button variant="ghost" onClick={cancelEdit}>
                Cancel
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderApprovedPanel = () => {
    if (!selectedSession) return null;
    return (
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
          <div className="flex items-center gap-3">
            <StatusBadge status="approved" />
            <span className="text-sm text-muted-foreground">
              Approved {selectedSession.approved_at
                ? formatDistanceToNow(new Date(selectedSession.approved_at), { addSuffix: true })
                : ''}
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-green-700 dark:text-green-400">
                  Plan approved — ready for output generation
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {selectedSession.sam_edits?.length
                    ? `${selectedSession.sam_edits.length} edit${selectedSession.sam_edits.length > 1 ? 's' : ''} were incorporated into this plan.`
                    : 'Cosmo\'s original thinking was approved without edits.'}
                </p>
              </div>
            </div>
          </div>

          {/* Outputs */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
              Outputs
            </p>
            {outputs.length === 0 ? (
              <div className="border border-dashed rounded-xl p-6 text-center space-y-2">
                <Sparkles className="h-6 w-6 text-muted-foreground mx-auto" />
                <p className="text-sm font-medium">No outputs generated yet</p>
                <p className="text-xs text-muted-foreground">
                  Outputs will appear here once the output-generation function is deployed.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {outputs.map(output => (
                  <div key={output.id} className="border rounded-xl p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className="text-xs capitalize">
                        {output.output_type.replace(/_/g, ' ')}
                      </Badge>
                      {output.sent_at && (
                        <span className="text-xs text-muted-foreground">
                          Sent {formatDistanceToNow(new Date(output.sent_at), { addSuffix: true })}
                        </span>
                      )}
                    </div>
                    <pre className="text-sm whitespace-pre-wrap font-sans leading-relaxed">
                      {typeof output.output_content === 'string'
                        ? output.output_content
                        : JSON.stringify(output.output_content, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Approved thinking summary */}
          <details className="group">
            <summary className="text-xs font-semibold uppercase tracking-wide text-muted-foreground cursor-pointer list-none flex items-center gap-2 py-1">
              <ChevronRight className="h-3.5 w-3.5 transition-transform group-open:rotate-90" />
              View approved thinking
            </summary>
            <div className="mt-3 space-y-3">
              {SECTIONS.map(section => {
                const content = parseThinking(selectedSession.thinking_text);
                const value = content[section.key];
                const Icon = section.icon;
                return (
                  <div key={section.key} className="border rounded-lg p-3 opacity-80">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {section.label}
                      </p>
                    </div>
                    {section.isList ? (
                      <ul className="space-y-1">
                        {(value as string[]).map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <span className="mt-1.5 h-1 w-1 rounded-full bg-muted-foreground shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-muted-foreground">{value as string}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </details>
        </div>
      </div>
    );
  };

  const renderRejectedPanel = () => {
    if (!selectedSession) return null;
    return (
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex items-center gap-3 px-6 py-4 border-b shrink-0">
          <StatusBadge status="rejected" />
        </div>
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center space-y-4">
          <div className="h-12 w-12 rounded-full bg-red-500/10 flex items-center justify-center">
            <X className="h-6 w-6 text-red-500" />
          </div>
          <div>
            <p className="font-medium">Thinking dismissed</p>
            {selectedSession.rejection_reason && (
              <p className="text-sm text-muted-foreground mt-1">
                Reason: {selectedSession.rejection_reason}
              </p>
            )}
          </div>
          <Button
            variant="outline"
            onClick={() => {
              setActiveView('empty');
              setSelectedSession(null);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Generate New Thinking
          </Button>
        </div>
      </div>
    );
  };

  // ── Main render ───────────────────────────────────────────────────

  if (loadingData) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] -my-6">
      {/* Page header */}
      <div className="flex items-center gap-3 px-6 py-4 border-b bg-card shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(orgId ? `/admin/org/${orgId}` : '/admin')}
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          {orgName || 'Back'}
        </Button>
        <div className="h-4 w-px bg-border" />
        <div className="flex items-center gap-2">
          <Brain className="h-4 w-4 text-primary" />
          <h1 className="text-base font-semibold">Cosmo's Thinking Layer</h1>
        </div>
        {orgName && (
          <span className="text-sm text-muted-foreground">— {orgName}</span>
        )}
      </div>

      {/* Two-panel layout */}
      <div className="flex flex-1 min-h-0">

        {/* ── Left panel: session history ── */}
        <div className="w-72 shrink-0 border-r flex flex-col">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Thinking Sessions
            </span>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={() => {
                setSelectedSession(null);
                setActiveView('empty');
              }}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              New
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {sessions.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-6 px-3">
                No thinking sessions yet. Generate one to get started.
              </p>
            ) : (
              sessions.map(session => {
                const meta = STATUS_META[session.status] ?? STATUS_META.draft;
                const Icon = meta.icon;
                const isActive = selectedSession?.id === session.id;
                return (
                  <button
                    key={session.id}
                    onClick={() => selectSession(session)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors space-y-1 ${
                      isActive ? 'bg-muted' : 'hover:bg-muted/60'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium truncate">
                        {TRIGGER_OPTIONS.find(t => t.value === session.trigger_type)?.label ?? session.trigger_type}
                      </span>
                      <Icon className={`h-3.5 w-3.5 shrink-0 ${
                        session.status === 'approved' ? 'text-green-500' :
                        session.status === 'rejected' ? 'text-red-500' : 'text-yellow-500'
                      }`} />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(session.created_at), { addSuffix: true })}
                    </p>
                    {session.sam_edits?.length > 0 && (
                      <p className="text-xs text-muted-foreground/70">
                        {session.sam_edits.length} edit{session.sam_edits.length > 1 ? 's' : ''}
                      </p>
                    )}
                  </button>
                );
              })
            )}
          </div>

          {/* Knowledge store hint */}
          <div className="border-t px-4 py-3">
            <button
              className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => navigate(`/admin/org/${orgId}`)}
            >
              <AlertCircle className="h-3.5 w-3.5" />
              View org knowledge store
            </button>
          </div>
        </div>

        {/* ── Right panel: active view ── */}
        <div className="flex-1 flex flex-col min-w-0">
          {activeView === 'empty'      && renderGeneratePanel()}
          {activeView === 'generating' && renderGeneratingState()}
          {activeView === 'preview'    && renderPreviewPanel()}
          {activeView === 'editing'    && renderEditPanel()}
          {activeView === 'approved'   && renderApprovedPanel()}
          {activeView === 'rejected'   && renderRejectedPanel()}
        </div>
      </div>
    </div>
  );
}
